import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  ISpirometrySession,
  ISpirometrySessionDetails,
  ISpirometrySessionsFilters,
} from "../../../@types/admin/AdminSpirometry";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { spirometryService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import { HttpError } from "../../../utils/HttpError";

export const SPIROMETRY_SESSIONS_QUERY_KEY = "admin-spirometry-sessions";

/** A page of spirometry sessions, newest first. Server-paginated. */
export function useSpirometrySessions(filters: ISpirometrySessionsFilters) {
  const { data, isLoading, isFetching, isError } = useQuery<
    IPaginatedResponse<ISpirometrySession[]>
  >({
    queryKey: [SPIROMETRY_SESSIONS_QUERY_KEY, filters],
    queryFn: async () => ensureOk(await spirometryService.getSessions(filters)),
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
  });

  return {
    sessions: data?.result ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
    isError,
  };
}

/** One session with every blow's assessment result. */
export function useSpirometrySession(id: number | undefined) {
  const { data, isLoading, isError, error } =
    useQuery<ISpirometrySessionDetails>({
      queryKey: [SPIROMETRY_SESSIONS_QUERY_KEY, "detail", id],
      queryFn: async () => ensureOk(await spirometryService.getSession(id!)),
      enabled: id !== undefined,
      refetchOnMount: "always",
    });

  return {
    session: data,
    isLoading,
    isError,
    isNotFound: error instanceof HttpError && error.status === 404,
  };
}
